/**
 * @fileoverview Gerenciamento de Empresas (groupName) → Contas (Company) → Usuários.
 *
 * Fluxo: superadmin lista Empresas (grupos) como cards; ao clicar abre detalhe
 * com 2 seções: Contas (Companies) e Usuários (todos os users do grupo).
 * Multi-conta de usuário via UserCompanies.
 */
import React, { useState, useEffect, useMemo, useCallback, useContext, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Tooltip } from "@material-ui/core";
import { Building2, Users, Plus, Pencil, Trash2, ChevronRight, ChevronDown, X, MessageCircle, Headphones, Sparkles, ShieldCheck, ShieldOff, RotateCcw, Search, Eye, EyeOff, Check, Info, LogIn, UserRoundCheck, Settings } from "lucide-react";
import ConfirmationModal from "../ConfirmationModal";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import UsersTable from "../UsersTable";
import ContaSelectorModal from "./ContaSelectorModal";
import UserDeleteImpactModal from "../UserDeleteImpactModal";

const ACCESS_SCOPES = [
  { value: "full", label: "Completo (Chat + Atendimento)", Icon: Sparkles, color: "#5B7CFF" },
  { value: "internal", label: "Apenas Chat Interno", Icon: MessageCircle, color: "#10B981" },
  { value: "attendance", label: "Apenas Atendimento", Icon: Headphones, color: "#F59E0B" },
];

// Máscara progressiva de CNPJ (00.000.000/0000-00). Armazenamos só dígitos.
const maskCnpj = (v) => {
  const d = (v || "").replace(/\D/g, "").slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    // Layout de página: o scroll vertical é controlado pelo MainContainer
    // pai. Sem maxHeight/overflowY aqui — evita scroll duplo e mantém o shell
    // atmosférico do tema dark visível atrás do conteúdo.
    //
    // paddingBottom + spacer-div (.bottomSpacer) garantem respiro premium.
    // Por que os dois: padding-bottom em flex column tem quirk em alguns
    // browsers — quando o conteúdo ultrapassa a viewport e entra em scroll,
    // o padding pode ser ignorado no extremo do scroll. O spacer-div força
    // a altura real e é à prova de quirks.
    root: {
      padding: theme.spacing(4),
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(14),
      minHeight: "100%",
      [theme.breakpoints.down("sm")]: {
        padding: theme.spacing(2.5),
        paddingBottom: theme.spacing(10),
      },
    },
    bottomSpacer: {
      height: theme.spacing(12),
      flexShrink: 0,
      [theme.breakpoints.down("sm")]: { height: theme.spacing(8) },
    },
    listHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      gap: 16,
      marginBottom: theme.spacing(3),
      paddingBottom: theme.spacing(2),
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
      flexWrap: "wrap",
    },
    listTitle: { fontSize: 26, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", letterSpacing: "-0.6px", lineHeight: 1.15 },
    listSub: { fontSize: 13, color: dark ? "#A1A1AA" : "#71717A", marginTop: 6, fontVariantNumeric: "tabular-nums" },
    // Estilos espelham `actionBtnBase` / `actionBtnPrimary` / `actionBtnSecondary`
    // da página /Contacts — fonte do padrão de botões secundários da plataforma.
    // ── Botões: alinhados ao padrão TicketsManagerTabs filter dialog ──
    // primary = gradient azul-violeta, ghost = transparente com texto cinza,
    // danger = gradient vermelho. textTransform "none" e radius 12.
    primaryBtn: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 36,
      minWidth: 0,
      padding: "0 14px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      border: "none",
      fontFamily: "inherit",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "& svg": { flexShrink: 0 },
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    ghostBtn: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 38,
      minWidth: 0,
      padding: "0 18px",
      borderRadius: 12,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background 180ms, border-color 180ms, color 180ms",
      background: "transparent",
      color: dark ? "#94a3b8" : "#6b7280",
      border: `1px solid ${dark ? "rgba(255, 255, 255, 0.10)" : "rgba(15, 23, 42, 0.10)"}`,
      "& svg": { flexShrink: 0 },
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
        borderColor: dark ? "rgba(91, 124, 255, 0.30)" : "rgba(61, 92, 253, 0.28)",
        color: dark ? "#e2e8f0" : "#374151",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    dangerBtn: {
      whiteSpace: "nowrap",
      flexShrink: 0,
      height: 38,
      minWidth: 0,
      padding: "0 18px",
      borderRadius: 12,
      fontSize: "0.875rem",
      fontWeight: 600,
      textTransform: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      cursor: "pointer",
      border: "none",
      fontFamily: "inherit",
      transition: "background 180ms, box-shadow 180ms",
      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
      color: "#ffffff",
      boxShadow: "0 4px 12px rgba(220,38,38,0.20)",
      "& svg": { flexShrink: 0 },
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
        boxShadow: "0 6px 16px rgba(220,38,38,0.30)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" },
    },
    cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 },
    empresaCard: {
      padding: 20, borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
      background: dark ? "rgba(255,255,255,0.02)" : "#fff",
      cursor: "pointer",
      transition: "transform 0.2s, border-color 0.2s, box-shadow 0.2s",
      willChange: "transform, box-shadow",
      "&:hover": { transform: "translateY(-2px)", borderColor: dark ? "rgba(91,124,255,0.3)" : "rgba(61,92,253,0.3)", boxShadow: dark ? "0 8px 24px rgba(91,124,255,0.1)" : "0 8px 24px rgba(61,92,253,0.08)" },
    },
    empresaCardHeader: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
    empresaIconBox: { width: 40, height: 40, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)", color: dark ? "#5B7CFF" : "#3D5CFD", flexShrink: 0 },
    empresaIconBoxLarge: { width: 56, height: 56, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)", color: dark ? "#5B7CFF" : "#3D5CFD", flexShrink: 0 },
    empresaCardName: { fontSize: 17, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" },
    empresaCardSub: { fontSize: 12, color: dark ? "#71717A" : "#A1A1AA", marginTop: 2 },
    statsRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, paddingTop: 16, borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` },
    statBox: { textAlign: "center" },
    statValue: { fontSize: 18, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" },
    statLabel: { fontSize: 10, fontWeight: 600, color: dark ? "#71717A" : "#A1A1AA", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 2 },
    backRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 24 },
    backBtn: { display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px", border: "none", borderRadius: 10, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", color: dark ? "#A1A1AA" : "#71717A", fontSize: 13, fontWeight: 600, cursor: "pointer", "&:hover": { background: dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)" } },
    detailHeader: { display: "flex", alignItems: "center", gap: 16, padding: 24, borderRadius: 14, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`, background: dark ? "rgba(255,255,255,0.02)" : "#fff", marginBottom: 24 },
    detailHeaderInfo: { flex: 1 },
    detailName: { fontSize: 26, fontWeight: 800, color: dark ? "#FAFAFA" : "#09090B", letterSpacing: "-0.5px" },
    detailSub: { fontSize: 13, color: dark ? "#A1A1AA" : "#71717A", marginTop: 4 },
    detailStats: { display: "flex", gap: 24, marginTop: 16 },
    section: { marginBottom: 32 },
    sectionHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 },
    sectionTitle: { fontSize: 18, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", display: "flex", alignItems: "center", gap: 10 },
    sectionCount: { fontSize: 13, fontWeight: 600, color: dark ? "#A1A1AA" : "#71717A", padding: "2px 10px", background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", borderRadius: 8 },
    listRow: { display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 10, borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, transition: "background 0.15s", "&:hover": { background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" } },
    rowAvatar: { width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14, fontWeight: 700, flexShrink: 0 },
    rowInfo: { flex: 1, minWidth: 0 },
    rowName: { fontSize: 14, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" },
    rowMeta: { fontSize: 12, color: dark ? "#71717A" : "#A1A1AA", marginTop: 2, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    scopeBadge: { display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700 },
    rowActions: { display: "flex", gap: 4 },
    iconBtn: { width: 32, height: 32, border: "none", background: "transparent", borderRadius: 8, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: dark ? "#A1A1AA" : "#71717A", transition: "background 0.15s, color 0.15s", "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" } },
    danger: { "&:hover": { background: "rgba(239,68,68,0.1)", color: "#EF4444" } },
    contasGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 },
    contaCard: { padding: 16, borderRadius: 12, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`, background: dark ? "rgba(255,255,255,0.02)" : "#fff" },
    contaName: { fontSize: 15, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", display: "flex", alignItems: "center", gap: 8 },
    contaStats: { display: "flex", flexDirection: "column", gap: 4, marginTop: 10, fontSize: 12, color: dark ? "#A1A1AA" : "#71717A" },
    contaActions: { display: "flex", gap: 4, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}`, justifyContent: "flex-end" },
    emptyState: { textAlign: "center", padding: 48, color: dark ? "#71717A" : "#A1A1AA", fontSize: 14, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 12, border: `1px dashed ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` },
    // ── Empty state premium para a listagem inicial ──
    emptyHero: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: "72px 24px",
      borderRadius: 16,
      border: `1px dashed ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
      background: dark
        ? "linear-gradient(180deg, rgba(91,124,255,0.04) 0%, rgba(255,255,255,0.01) 100%)"
        : "linear-gradient(180deg, rgba(61,92,253,0.03) 0%, rgba(255,255,255,0) 100%)",
    },
    emptyHeroIcon: {
      width: 64,
      height: 64,
      borderRadius: 18,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 18,
      background: dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.10)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      boxShadow: dark
        ? "0 0 0 1px rgba(91,124,255,0.18), 0 8px 24px rgba(91,124,255,0.10)"
        : "0 0 0 1px rgba(61,92,253,0.12), 0 8px 24px rgba(61,92,253,0.08)",
    },
    emptyHeroTitle: { fontSize: 18, fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B", letterSpacing: "-0.3px" },
    emptyHeroSub: { fontSize: 13, color: dark ? "#A1A1AA" : "#71717A", marginTop: 6, maxWidth: 360, lineHeight: 1.55 },
    emptyHeroCta: { marginTop: 22 },
    // ── Skeleton de carregamento ──
    skeletonCard: {
      padding: 20,
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)"}`,
      background: dark ? "rgba(255,255,255,0.02)" : "#fff",
      position: "relative",
      overflow: "hidden",
      "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: dark
          ? "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%)"
          : "linear-gradient(90deg, transparent 0%, rgba(15,23,42,0.04) 50%, transparent 100%)",
        animation: "$shimmer 1.4s ease-in-out infinite",
      },
    },
    skeletonRow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 16 },
    skeletonBlock: {
      borderRadius: 8,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)",
    },
    skeletonFooter: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, paddingTop: 16, borderTop: `1px solid ${dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)"}` },
    "@keyframes shimmer": {
      "0%": { transform: "translateX(-100%)" },
      "100%": { transform: "translateX(100%)" },
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Tracking-style list — cards grandes (1-2 por linha) com 3 stats
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    trackingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(440px, 1fr))",
      gap: 18,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    trackingCard: {
      padding: 24,
      borderRadius: 16,
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)"}`,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      cursor: "pointer",
      transition: "border-color .2s ease, box-shadow .2s ease, transform .2s ease",
      display: "flex",
      flexDirection: "column",
      gap: 20,
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.28)" : "rgba(61,92,253,0.28)",
        boxShadow: dark
          ? "0 10px 28px rgba(91,124,255,0.08)"
          : "0 10px 28px rgba(61,92,253,0.06)",
        transform: "translateY(-1px)",
      },
    },
    trackingCardHeader: { display: "flex", alignItems: "flex-start", gap: 16 },
    trackingIconBox: {
      width: 48,
      height: 48,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(91,124,255,0.14)" : "rgba(61,92,253,0.10)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      border: `1px solid ${dark ? "rgba(91,124,255,0.22)" : "rgba(61,92,253,0.18)"}`,
    },
    trackingCardTitle: { flex: 1, minWidth: 0 },
    trackingCardName: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.4px",
      lineHeight: 1.2,
    },
    trackingSistemaBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 10px",
      borderRadius: 6,
      background: dark ? "rgba(251,191,36,0.10)" : "rgba(245,158,11,0.10)",
      color: dark ? "#fbbf24" : "#b45309",
      border: `1px solid ${dark ? "rgba(251,191,36,0.30)" : "rgba(245,158,11,0.32)"}`,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.2,
      marginTop: 8,
    },
    trackingStatsRow: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
    },
    trackingStatBox: {
      padding: "16px 12px",
      borderRadius: 12,
      border: `1px solid ${dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)"}`,
      background: dark ? "rgba(255,255,255,0.018)" : "rgba(15,23,42,0.018)",
      textAlign: "center",
    },
    trackingStatValue: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.3px",
      lineHeight: 1.1,
    },
    trackingStatLabel: {
      fontSize: 11,
      color: dark ? "#9CA3AF" : "#6B7280",
      marginTop: 4,
      fontWeight: 500,
    },
    // Linha de ações ao pé do card de Empresa — duas ações lado a lado.
    trackingActionsRow: {
      marginTop: 16,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 8,
    },
    trackingActionsRowSingle: {
      marginTop: 16,
      display: "grid",
      gridTemplateColumns: "1fr",
    },
    trackingAccessBtn: {
      padding: "10px 12px",
      borderRadius: 12,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      transition: "transform .12s, opacity .15s",
      fontFamily: "inherit",
      minWidth: 0,
      "&:hover:not(:disabled)": { transform: "translateY(-1px)" },
      "&:active": { transform: "translateY(0)" },
      "&:disabled": { opacity: 0.7, cursor: "wait" },
    },
    trackingManageBtn: {
      padding: "10px 12px",
      borderRadius: 12,
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      transition: "background .12s, border-color .12s, transform .12s",
      fontFamily: "inherit",
      minWidth: 0,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)",
        borderColor: dark ? "rgba(91,124,255,0.40)" : "rgba(61,92,253,0.30)",
        transform: "translateY(-1px)",
      },
      "&:active": { transform: "translateY(0)" },
    },
    trackingBtnLabel: {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    // Estado "Conta ativa" — verde sutil, cursor default. Quando a Empresa
    // tem múltiplas contas, vira clicável (abre seletor pra trocar) sem
    // perder o feedback visual de que já estamos dentro dela.
    trackingActiveBtn: {
      padding: "10px 12px",
      borderRadius: 12,
      background: dark ? "rgba(34,197,94,0.12)" : "rgba(34,197,94,0.08)",
      border: `1px solid ${dark ? "rgba(34,197,94,0.32)" : "rgba(34,197,94,0.22)"}`,
      color: dark ? "#86efac" : "#16a34a",
      fontSize: 13,
      fontWeight: 600,
      cursor: "default",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      fontFamily: "inherit",
      minWidth: 0,
      transition: "background .12s, border-color .12s",
    },
    trackingActiveBtnClickable: {
      cursor: "pointer",
      "&:hover": {
        background: dark ? "rgba(34,197,94,0.18)" : "rgba(34,197,94,0.12)",
        borderColor: dark ? "rgba(34,197,94,0.45)" : "rgba(34,197,94,0.32)",
      },
    },
    trackingActiveDot: {
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: "#22c55e",
      boxShadow: "0 0 0 2px rgba(34,197,94,0.25)",
      flexShrink: 0,
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Detalhe — header novo + KPI cards
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    detailHeaderV2: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      marginBottom: 28,
      flexWrap: "wrap",
    },
    detailBackBtn: {
      width: 40,
      height: 40,
      borderRadius: 10,
      border: "none",
      background: "transparent",
      color: dark ? "#A1A1AA" : "#71717A",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background .15s, color .15s",
      flexShrink: 0,
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
        color: dark ? "#FAFAFA" : "#09090B",
      },
    },
    detailTitleWrap: { flex: 1, minWidth: 0 },
    detailNameV2: {
      fontSize: 32,
      fontWeight: 800,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.8px",
      lineHeight: 1.1,
    },
    detailSubV2: { fontSize: 13, color: dark ? "#71717A" : "#A1A1AA", marginTop: 6 },
    detailActionsV2: { display: "flex", gap: 8, alignItems: "center", flexShrink: 0 },
    detailEditBtn: {
      height: 36,
      padding: "0 16px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.12)"}`,
      background: dark ? "rgba(255,255,255,0.05)" : "#fff",
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background .15s, border-color .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.04)",
        borderColor: dark ? "rgba(91,124,255,0.4)" : "rgba(61,92,253,0.32)",
      },
    },
    detailDeleteBtn: {
      height: 36,
      padding: "0 16px",
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(239,68,68,0.32)" : "rgba(220,38,38,0.28)"}`,
      background: "transparent",
      color: dark ? "#fca5a5" : "#dc2626",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "background .15s, border-color .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(239,68,68,0.08)" : "rgba(220,38,38,0.06)",
        borderColor: dark ? "rgba(239,68,68,0.5)" : "rgba(220,38,38,0.45)",
      },
    },
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
      gap: 14,
      marginBottom: 32,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    kpiCard: {
      padding: 22,
      borderRadius: 14,
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)"}`,
      background: dark ? "rgba(255,255,255,0.022)" : "#fff",
    },
    kpiCardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      minWidth: 0,
    },
    kpiIconChip: {
      width: 32,
      height: 32,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    kpiInfoBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "help",
      color: dark ? "#52525B" : "#A1A1AA",
      marginLeft: "auto",
      flexShrink: 0,
      width: 22,
      height: 22,
      borderRadius: 6,
      transition: "color .15s, background .15s",
      "&:hover": {
        color: dark ? "#A1A1AA" : "#71717A",
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      },
    },
    kpiLabel: {
      fontSize: 12,
      fontWeight: 500,
      color: dark ? "#9CA3AF" : "#6B7280",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    kpiValueRow: { display: "flex", alignItems: "baseline", gap: 8 },
    kpiValue: {
      fontSize: 30,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      fontVariantNumeric: "tabular-nums",
      letterSpacing: "-0.6px",
      lineHeight: 1,
    },
    kpiValueSub: {
      fontSize: 15,
      color: dark ? "#71717A" : "#A1A1AA",
      fontWeight: 500,
      fontVariantNumeric: "tabular-nums",
    },
    kpiOverBar: {
      marginTop: 12,
      height: 4,
      borderRadius: 999,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    kpiOverFill: {
      height: "100%",
      background: "linear-gradient(90deg, #4f7cff 0%, #3b82f6 100%)",
      borderRadius: 999,
      transition: "width .3s ease",
    },
    kpiOverFillWarn: {
      background: "linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)",
    },
    kpiUnlimited: {
      display: "inline-block",
      marginTop: 8,
      fontSize: 11,
      color: dark ? "#71717A" : "#A1A1AA",
      fontWeight: 500,
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Section + Search
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    sectionV2: { marginBottom: 36 },
    sectionHeaderV2: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginBottom: 16,
      gap: 16,
      flexWrap: "wrap",
    },
    sectionTitleWrapV2: { display: "flex", alignItems: "baseline", gap: 10 },
    sectionTitleV2: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.4px",
    },
    sectionCountV2: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#71717A" : "#A1A1AA",
      fontVariantNumeric: "tabular-nums",
    },
    sectionActionsV2: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    searchWrap: { position: "relative", marginBottom: 16 },
    searchInput: {
      width: "100%",
      height: 44,
      paddingLeft: 44,
      paddingRight: 16,
      borderRadius: 12,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)"}`,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 14,
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color .15s, background .15s, box-shadow .15s",
      "&:focus": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark
          ? "0 0 0 3px rgba(91,124,255,0.15)"
          : "0 0 0 3px rgba(61,92,253,0.12)",
      },
      "&::placeholder": { color: dark ? "#71717A" : "#A1A1AA" },
    },
    searchIcon: {
      position: "absolute",
      left: 14,
      top: "50%",
      transform: "translateY(-50%)",
      color: dark ? "#71717A" : "#A1A1AA",
      pointerEvents: "none",
      display: "flex",
    },

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // Pagination
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    pagWrap: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      marginTop: 20,
    },
    pagBtn: {
      width: 36,
      height: 36,
      borderRadius: 10,
      border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)"}`,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      color: dark ? "#A1A1AA" : "#71717A",
      fontSize: 13,
      fontWeight: 600,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      transition: "background .15s, border-color .15s, color .15s",
      padding: 0,
      fontFamily: "inherit",
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#09090B",
      },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    },
    pagBtnActive: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      borderColor: "transparent",
      boxShadow: "0 4px 12px rgba(61,92,253,0.25)",
      "&:hover": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
        color: "#fff",
      },
    },

    // ── Modal shell: alinhado ao TicketsManagerTabs filterDialog + QueueModal ──
    // Mesmo padrão visual usado no Chat de Atendimento: gradient dark suave,
    // border azul-violeta (#5B7CFF rgba 0.2), borderRadius 24, shadow profunda.
    // Light mode mantém o look limpo branco.
    modalBackdrop: {
      position: "fixed",
      inset: 0,
      background: dark ? "rgba(0,0,0,0.78)" : "rgba(15, 23, 42, 0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1300,
      padding: 16,
    },
    modalCard: {
      width: "100%",
      maxWidth: 520,
      background: dark
        ? "linear-gradient(135deg, rgba(18,18,19,0.98) 0%, rgba(22,22,24,0.98) 100%)"
        : "#ffffff",
      borderRadius: 24,
      border: `1px solid ${dark ? "rgba(91,124,255,0.20)" : "rgba(61,92,253,0.18)"}`,
      boxShadow: dark
        ? "0 20px 60px rgba(0,0,0,0.55)"
        : "0 20px 60px rgba(15,23,42,0.12)",
      overflow: "hidden",
      maxHeight: "92vh",
      display: "flex",
      flexDirection: "column",
    },
    modalHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "16px 22px 12px",
      borderBottom: `1px solid ${dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.10)"}`,
    },
    modalTitle: {
      fontSize: "1.125rem",
      fontWeight: 600,
      color: dark ? "#f1f5f9" : "#0f172a",
      letterSpacing: "-0.2px",
    },
    modalBody: { padding: "20px 22px", overflowY: "auto", flex: 1 },
    field: { marginBottom: 14 },
    fieldLabel: {
      display: "block",
      fontSize: 12,
      fontWeight: 500,
      color: dark ? "#94a3b8" : "#475569",
      marginBottom: 6,
      letterSpacing: 0.1,
    },
    // ── Field outlined: alinhado a QueueModal.fieldOutlined e ModernSelect ──
    // Mesma fórmula visual do sistema: background suave, border azul-violeta
    // discreta, focus com glow 4px (0 0 0 4px rgba(91,124,255,0.15)) +
    // border highlight. Light mode mantém legibilidade clean.
    fieldInput: {
      width: "100%",
      // height fixa garante que email, password, text e select fiquem com a
      // mesma altura visual (browsers aplicam line-height/padding diferente
      // por type).
      height: 44,
      padding: "0 14px",
      background: dark ? "rgba(255,255,255,0.05)" : "#ffffff",
      border: `1px solid ${dark ? "rgba(91,124,255,0.15)" : "rgba(15,23,42,0.12)"}`,
      borderRadius: 14,
      color: dark ? "#f1f5f9" : "#0f172a",
      fontSize: 14,
      fontFamily: "inherit",
      lineHeight: 1.4,
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.15s, background 0.15s, box-shadow 0.15s",
      // Reset cross-browser
      WebkitAppearance: "none",
      MozAppearance: "none",
      appearance: "none",
      "&::placeholder": {
        color: dark ? "rgba(248,250,252,0.45)" : "#94a3b8",
        opacity: 1,
      },
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.28)" : "rgba(61,92,253,0.22)",
      },
      "&:focus": {
        borderColor: dark ? "rgba(91,124,255,0.45)" : "rgba(61,92,253,0.40)",
        boxShadow: dark
          ? "0 0 0 4px rgba(91,124,255,0.15)"
          : "0 0 0 4px rgba(61,92,253,0.12)",
      },
      // Remove o "ring" amarelo/azul de autofill do Chrome
      "&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus": {
        WebkitBoxShadow: dark
          ? "0 0 0 1000px rgba(255,255,255,0.04) inset"
          : "0 0 0 1000px #ffffff inset",
        WebkitTextFillColor: dark ? "#FAFAFA" : "#09090B",
        caretColor: dark ? "#FAFAFA" : "#09090B",
        transition: "background-color 9999s ease-in-out 0s",
      },
    },
    fieldHint: { fontSize: 11, color: dark ? "#71717A" : "#A1A1AA", marginTop: 4 },
    fieldRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    checkboxList: { display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto", padding: 8, borderRadius: 10, background: dark ? "rgba(0,0,0,0.2)" : "rgba(0,0,0,0.02)", border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` },
    checkboxItem: { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", borderRadius: 8, cursor: "pointer", transition: "background 0.15s", "&:hover": { background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" } },
    modalActions: {
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      padding: "12px 22px 16px",
      borderTop: `1px solid ${dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.10)"}`,
    },
    multiSelectWrap: { position: "relative" },
    multiSelectTrigger: {
      width: "100%", padding: "10px 14px",
      background: dark ? "rgba(255,255,255,0.04)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 10,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 14, fontWeight: 500, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      transition: "border 0.15s",
      "&:hover": { borderColor: dark ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)" },
    },
    multiSelectLabel: { color: dark ? "#FAFAFA" : "#09090B", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    multiSelectMenu: {
      position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
      background: dark ? "#18181B" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
      borderRadius: 10,
      boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
      zIndex: 1400,
      padding: 4,
      maxHeight: 240,
      overflowY: "auto",
    },
    multiSelectItem: {
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px", borderRadius: 8, cursor: "pointer",
      transition: "background 0.1s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)" },
    },
    mfaCard: {
      marginTop: 4,
      padding: 14,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      background: dark ? "rgba(91, 124, 255, 0.06)" : "rgba(61, 92, 253, 0.04)",
      border: `1px solid ${dark ? "rgba(91, 124, 255, 0.18)" : "rgba(61, 92, 253, 0.12)"}`,
    },
    mfaCardLeft: {
      display: "flex", alignItems: "center", gap: 12, minWidth: 0,
    },
    mfaIconBadge: {
      width: 36, height: 36, borderRadius: 10,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    },
    mfaIconBadgeOn: {
      background: dark ? "rgba(16, 185, 129, 0.18)" : "rgba(16, 185, 129, 0.12)",
      color: dark ? "#34d399" : "#059669",
      border: `1px solid ${dark ? "rgba(16, 185, 129, 0.35)" : "rgba(16, 185, 129, 0.25)"}`,
    },
    mfaIconBadgeOff: {
      background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
      color: dark ? "rgba(255, 255, 255, 0.45)" : "#94a3b8",
      border: `1px solid ${dark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
    },
    mfaTextWrap: { display: "flex", flexDirection: "column", minWidth: 0 },
    mfaTitle: {
      fontSize: 13, fontWeight: 600,
      color: dark ? "#f1f5f9" : "#1e293b", lineHeight: 1.2,
    },
    mfaStatus: { fontSize: 11.5, fontWeight: 500, marginTop: 2 },
    mfaStatusOn: { color: dark ? "#34d399" : "#059669" },
    mfaStatusOff: { color: dark ? "rgba(255,255,255,0.5)" : "#64748b" },
    mfaResetBtn: {
      display: "inline-flex", alignItems: "center", gap: 6,
      height: 32, padding: "0 12px", borderRadius: 8,
      background: "transparent",
      color: dark ? "#fca5a5" : "#dc2626",
      border: `1px solid ${dark ? "rgba(248, 113, 113, 0.35)" : "rgba(220, 38, 38, 0.3)"}`,
      cursor: "pointer", fontSize: 12, fontWeight: 500,
      transition: "background 0.15s",
      "&:hover": {
        background: dark ? "rgba(248, 113, 113, 0.08)" : "rgba(220, 38, 38, 0.06)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
  };
});

// Select single-choice usando o mesmo design do multiSelect já no projeto
// (multiSelectTrigger/Menu/Item). Definido fora do componente principal pra
// não ser recriado a cada render e perder o estado de open/ref.
const SingleSelect = ({ classes, dark, value, onChange, options, placeholder, renderOption }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={classes.multiSelectWrap} ref={ref}>
      <button
        type="button"
        className={classes.multiSelectTrigger}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={classes.multiSelectLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {selected ? (renderOption ? renderOption(selected) : selected.label) : (placeholder || "Selecione...")}
        </span>
        <ChevronDown size={16} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s", flexShrink: 0 }} />
      </button>
      {open && (
        <div className={classes.multiSelectMenu}>
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={classes.multiSelectItem}
                onClick={() => { onChange(opt.value); setOpen(false); }}
              >
                <div style={{ flex: 1, fontSize: 13, color: dark ? "#FAFAFA" : "#09090B", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                  {renderOption ? renderOption(opt) : opt.label}
                </div>
                {isSelected && <Check size={14} style={{ color: dark ? "#5B7CFF" : "#3D5CFD", flexShrink: 0 }} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const CompaniesByGroup = () => {
  const theme = useTheme();
  const classes = useStyles();
  const dark = theme.palette.type === "dark";
  const { user: currentUser, impersonateUser, superSwitchCompany } = useContext(AuthContext);
  const canImpersonate = !!currentUser?.super;
  const [impersonatingId, setImpersonatingId] = useState(null);
  const [switchingEmpresaKey, setSwitchingEmpresaKey] = useState(null);
  // Empresa selecionada pro modal de "qual Conta acessar". null = modal fechado.
  const [contaPickerEmpresa, setContaPickerEmpresa] = useState(null);
  // Conta sendo trocada via card "Acessar Conta" — pra desabilitar enquanto roda.
  const [switchingContaId, setSwitchingContaId] = useState(null);

  const [view, setView] = useState("list");
  const [selectedGroupName, setSelectedGroupName] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingConta, setEditingConta] = useState(null);
  const [savingConta, setSavingConta] = useState(false);
  const [deletingConta, setDeletingConta] = useState(null);
  // Chave OpenAI GRÁTIS (teste) da conta — gerida pelo super-admin, invisível em /integrations.
  const [aiKey, setAiKey] = useState("");
  const [aiKeyMeta, setAiKeyMeta] = useState({ hasKey: false, apiKeyMask: null, isActive: false, loading: false });

  const [editingUser, setEditingUser] = useState(null);
  const [userContaIds, setUserContaIds] = useState([]);
  const [multiSelectOpen, setMultiSelectOpen] = useState(false);
  const multiSelectRef = useRef(null);
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [resettingMfa, setResettingMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  useEffect(() => { if (!editingUser) setShowPassword(false); }, [editingUser]);

  const [creatingEmpresa, setCreatingEmpresa] = useState(null);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [savingEmpresa, setSavingEmpresa] = useState(false);
  const [deletingEmpresa, setDeletingEmpresa] = useState(null);
  const [deletingEmpresaBusy, setDeletingEmpresaBusy] = useState(false);

  // ── Pagination ─────────────────────────────────────────────────
  const EMPRESAS_PER_PAGE = 10;
  const CONTAS_PER_PAGE = 10;
  const USERS_PER_PAGE = 10;
  const [empresasPage, setEmpresasPage] = useState(1);
  const [contasPage, setContasPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const [contasSearch, setContasSearch] = useState("");
  const [usersSearch, setUsersSearch] = useState("");

  // ESC fecha apenas o modal de Conta (Adicionar/Editar). Modal de Usuário
  // não fecha com ESC para evitar perda acidental de dados no formulário maior.
  useEffect(() => {
    if (!editingConta) return undefined;
    const handler = (e) => {
      if (e.key === "Escape" && !savingConta) {
        setEditingConta(null);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [editingConta, savingConta]);

  // groupsUsage: { "<groupName>": { contas, interno, atendimento, total } }
  // Cada métrica é `{ used, limit }`. Server-side aggregate, fonte da verdade
  // dos KPI cards. Multi-conta não consome cobrança extra — cada usuário é
  // contado pela sua Conta primária.
  const [groupsUsage, setGroupsUsage] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // group-users (super only): retorna todos os users sem filtrar pela
      // Conta primária do super-admin — diferente de GET /users que filtra
      // via UserCompany. A tabela aqui mostra users de TODAS as Contas do
      // grupo, então precisamos da lista completa.
      const [companiesRes, usersRes, usageRes] = await Promise.all([
        api.get("/companies/list"),
        api.get("/companies/group-users"),
        api.get("/companies/groups-usage").catch(() => ({ data: {} })),
      ]);
      const cs = companiesRes.data;
      const us = usersRes.data;
      setCompanies(Array.isArray(cs) ? cs : (cs?.companies || []));
      setUsers(Array.isArray(us) ? us : (us?.users || []));
      setGroupsUsage(usageRes.data || {});
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fecha o dropdown multi-select ao clicar fora
  useEffect(() => {
    if (!multiSelectOpen) return;
    const onClick = (e) => {
      if (multiSelectRef.current && !multiSelectRef.current.contains(e.target)) {
        setMultiSelectOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [multiSelectOpen]);

  // Agrupa Companies por groupName em Empresas. Stats agregados (contas e
  // usuários) vêm do server via groupsUsage — aqui só derivamos contas,
  // isSystem e a data de criação mais antiga (header do detalhe).
  const empresas = useMemo(() => {
    const map = new Map();
    companies.forEach((c) => {
      const key = c.groupName || "__sem_grupo__";
      if (!map.has(key)) {
        map.set(key, {
          groupName: c.groupName || null,
          displayName: c.groupName || "Sem grupo",
          contas: [],
          isSystem: false,
          earliestCreatedAt: null,
        });
      }
      const empresa = map.get(key);
      empresa.contas.push(c);
      // Sistema = grupo que contém a Conta host (id=1).
      if (c.id === 1) empresa.isSystem = true;
      const created = c.createdAt ? new Date(c.createdAt) : null;
      if (created && (!empresa.earliestCreatedAt || created < empresa.earliestCreatedAt)) {
        empresa.earliestCreatedAt = created;
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      // Sistema sempre no topo (referência Tracking)
      if (a.isSystem && !b.isSystem) return -1;
      if (!a.isSystem && b.isSystem) return 1;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [companies, users]);

  const selectedEmpresa = useMemo(() => empresas.find(e => e.groupName === selectedGroupName) || null, [empresas, selectedGroupName]);

  // ── Editar/Excluir Empresa ──────────────────────────────────────────
  // Editar = rename + atualizar limites de grupo (denormalizados nas Contas).
  // Backend não tem rota dedicada de "rename/edit empresa" — atualizamos
  // cada Conta individualmente. Pesado em grupos grandes mas robusto.
  const handleSaveEditEmpresa = async () => {
    if (!editingEmpresa?.name?.trim() || !editingEmpresa._originalGroupName) return;
    const newName = editingEmpresa.name.trim();
    const payloadGroupLimits = {
      maxAccountsInGroup: editingEmpresa.maxAccountsInGroup ?? null,
      maxInternalChatUsersInGroup: editingEmpresa.maxInternalChatUsersInGroup ?? null,
      maxAttendanceUsersInGroup: editingEmpresa.maxAttendanceUsersInGroup ?? null,
      maxTotalUsersInGroup: editingEmpresa.maxTotalUsersInGroup ?? null,
      billingExemptInGroup: !!editingEmpresa.billingExemptInGroup,
      onboardingDisabled: !!editingEmpresa.onboardingDisabled,
    };
    setSavingEmpresa(true);
    try {
      const targetContas = companies.filter(c => (c.groupName || null) === editingEmpresa._originalGroupName);
      await Promise.all(targetContas.map(c =>
        api.put(`/companies/${c.id}`, {
          ...c,
          groupName: newName,
          ...payloadGroupLimits,
          // Conta principal de cobrança: true só na escolhida (false nas demais)
          // → garante 1 por Empresa.
          isBillingPrimaryInGroup: c.id === editingEmpresa.primaryBillingCompanyId,
        })
      ));
      toast.success("Empresa atualizada");
      if (selectedGroupName === editingEmpresa._originalGroupName) {
        setSelectedGroupName(newName);
      }
      await fetchData();
      setEditingEmpresa(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEmpresa(false);
    }
  };

  const handleDeleteEmpresa = async () => {
    if (!deletingEmpresa) return;
    setDeletingEmpresaBusy(true);
    try {
      const targetContas = companies.filter(c => (c.groupName || null) === deletingEmpresa.groupName);
      // Exclui todas as Contas do grupo. Cada DELETE tem CASCADE no model
      // (Users, Whatsapps, Tickets, etc).
      await Promise.all(targetContas.map(c => api.delete(`/companies/${c.id}`)));
      toast.success("Empresa excluída");
      await fetchData();
      // Volta pra lista
      setSelectedGroupName(null);
      setView("list");
      setDeletingEmpresa(null);
    } catch (err) {
      toastError(err);
    } finally {
      setDeletingEmpresaBusy(false);
    }
  };

  // ── Pagination helpers ────────────────────────────────────────────────
  const paginate = (arr, page, size) => {
    const start = (page - 1) * size;
    return arr.slice(start, start + size);
  };
  const totalPages = (total, size) => Math.max(1, Math.ceil(total / size));

  // Reset de página ao mudar filtros/seleção
  useEffect(() => { setContasPage(1); setUsersPage(1); }, [selectedGroupName]);
  useEffect(() => { setContasPage(1); }, [contasSearch]);
  useEffect(() => { setUsersPage(1); }, [usersSearch]);
  useEffect(() => { setEmpresasPage(1); }, [empresas.length]);

  // Componente local de paginação (1, 2, 3 ... < >)
  const renderPagination = (current, total, onChange) => {
    if (total <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);
    start = Math.max(1, Math.min(start, end - maxVisible + 1));
    for (let i = start; i <= end; i++) pages.push(i);
    return (
      <div className={classes.pagWrap}>
        <button
          className={classes.pagBtn}
          onClick={() => onChange(current - 1)}
          disabled={current === 1}
          aria-label="Página anterior"
        >
          <ChevronRight size={16} style={{ transform: "rotate(180deg)" }} />
        </button>
        {pages.map(p => (
          <button
            key={p}
            className={`${classes.pagBtn} ${p === current ? classes.pagBtnActive : ""}`}
            onClick={() => onChange(p)}
            aria-current={p === current ? "page" : undefined}
          >
            {p}
          </button>
        ))}
        <button
          className={classes.pagBtn}
          onClick={() => onChange(current + 1)}
          disabled={current === total}
          aria-label="Próxima página"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    );
  };

  // ============== CONTA ==============
  const resetAiKeyState = (loading = false) => {
    setAiKey("");
    setAiKeyMeta({ hasKey: false, apiKeyMask: null, isActive: false, loading });
  };

  const handleOpenCreateConta = () => {
    setEditingConta({ name: "", _isNew: true });
    resetAiKeyState(false);
  };

  const handleOpenEditConta = (conta) => {
    setEditingConta({ ...conta });
    resetAiKeyState(true);
    api
      .get(`/companies/${conta.id}/ai-key`)
      .then(({ data }) =>
        setAiKeyMeta({
          hasKey: !!data.hasKey,
          apiKeyMask: data.apiKeyMask || null,
          isActive: !!data.isActive,
          loading: false,
        })
      )
      .catch(() => resetAiKeyState(false));
  };

  const handleSaveConta = async () => {
    if (!editingConta?.name?.trim()) return;
    setSavingConta(true);
    try {
      let contaId = editingConta.id;
      // Whitelist explícita (NÃO spread de editingConta — evita reenviar limites/
      // cortesia/status). CNPJ vai só-dígitos.
      const fiscalFields = {
        razaoSocial: (editingConta.razaoSocial || "").trim(),
        nomeFantasia: (editingConta.nomeFantasia || "").trim(),
        cnpj: (editingConta.cnpj || "").replace(/\D/g, ""),
        phone: (editingConta.phone || "").trim(),
        email: (editingConta.email || "").trim(),
      };
      if (contaId) {
        await api.put(`/companies/${contaId}`, {
          name: editingConta.name,
          ...fiscalFields,
        });
      } else {
        const { data: created } = await api.post("/companies", {
          name: editingConta.name,
          groupName: selectedEmpresa?.groupName,
          isInternalChatChannel: true,
          ...fiscalFields,
        });
        contaId = created?.id;
      }
      // Chave de IA grátis (opcional): só envia quando o super-admin digitou.
      if (contaId && aiKey.trim()) {
        await api.put(`/companies/${contaId}/ai-key`, { apiKey: aiKey.trim() });
      }
      toast.success(editingConta.id ? "Conta atualizada" : "Conta criada");
      setEditingConta(null);
      await fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingConta(false);
    }
  };

  const handleRemoveAiKey = async () => {
    if (!editingConta?.id) return;
    try {
      await api.put(`/companies/${editingConta.id}/ai-key`, { apiKey: "" });
      resetAiKeyState(false);
      toast.success("Chave de IA removida");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteConta = async () => {
    if (!deletingConta) return;
    try {
      await api.delete(`/companies/${deletingConta.id}`);
      toast.success("Conta excluída");
      setDeletingConta(null);
      await fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  // ============== USUÁRIO ==============
  const handleOpenCreateUser = () => {
    const groupContas = companies.filter(c => (c.groupName || null) === selectedGroupName);
    const defaultConta = groupContas[0];
    setEditingUser({ name: "", email: "", password: "", profile: "user", accessScope: "full", companyId: defaultConta?.id, _isNew: true });
    setUserContaIds(defaultConta ? [defaultConta.id] : []);
  };

  const handleOpenEditUser = async (u) => {
    setEditingUser({ ...u, password: "" });
    try {
      const { data } = await api.get(`/users/${u.id}/companies`);
      const list = Array.isArray(data) ? data : (data?.companies || []);
      setUserContaIds(list.map(c => c.id));
    } catch {
      setUserContaIds(u.companyId ? [u.companyId] : []);
    }
  };

  const handleSaveUser = async () => {
    if (!editingUser?.name?.trim()) {
      toast.error("Nome é obrigatório"); return;
    }
    if (!editingUser?.email?.trim()) {
      toast.error("Email é obrigatório"); return;
    }
    if (editingUser._isNew && (!editingUser.password || editingUser.password.length < 6)) {
      toast.error("Senha precisa ter pelo menos 6 caracteres"); return;
    }
    if (userContaIds.length === 0) {
      toast.error("Selecione pelo menos uma Conta"); return;
    }

    // Conta principal precisa estar nas Contas vinculadas. Antes havia
    // fallback silencioso (`userContaIds[0]`) que enganava o admin: ele
    // selecionava "World" no select, desmarcava-a no checklist sem perceber,
    // e o save mascarava com outra Conta. Agora bloqueia explicitamente.
    if (!userContaIds.includes(editingUser.companyId)) {
      toast.error("A Conta principal selecionada não está nas Contas vinculadas.");
      return;
    }
    const primaryConta = editingUser.companyId;

    setSavingUser(true);
    try {
      if (editingUser.id) {
        const updateData = {
          name: editingUser.name,
          email: editingUser.email,
          profile: editingUser.profile,
          accessScope: editingUser.accessScope,
          companyId: primaryConta,
          maxCompanies: Math.max(userContaIds.length, editingUser.maxCompanies || 1),
        };
        if (editingUser.password) updateData.password = editingUser.password;
        // 1) PUT primeiro — atualiza User.companyId pra primary nova. Sem
        //    isto, o DELETE da antiga primary abaixo era bloqueado pelo
        //    ERR_CANNOT_REMOVE_ACTIVE_COMPANY (bug histórico do silent
        //    discard de companyId no UpdateUserService).
        await api.put(`/users/${editingUser.id}`, updateData);

        const { data: currentData } = await api.get(`/users/${editingUser.id}/companies`);
        const currentList = Array.isArray(currentData) ? currentData : (currentData?.companies || []);
        const currentIds = currentList.map(c => c.id);
        const toAdd = userContaIds.filter(id => !currentIds.includes(id));
        const toRemove = currentIds.filter(id => !userContaIds.includes(id));

        // 2) ADD antes de REMOVE — se um POST falhar (ex.: estouro de
        //    maxCompanies / company fora do grupo), o user mantém os
        //    vínculos atuais. Antes era um Promise.all unificado que dava
        //    pra remover acesso e ficar sem o novo no mesmo save.
        if (toAdd.length > 0) {
          await Promise.all(toAdd.map(id =>
            api.post(`/users/${editingUser.id}/companies`, { companyId: id })
          ));
        }
        if (toRemove.length > 0) {
          await Promise.all(toRemove.map(id =>
            api.delete(`/users/${editingUser.id}/companies/${id}`)
          ));
        }
        toast.success("Usuário atualizado");
      } else {
        const { data: newUser } = await api.post("/users", {
          name: editingUser.name,
          email: editingUser.email,
          password: editingUser.password,
          profile: editingUser.profile,
          accessScope: editingUser.accessScope,
          companyId: primaryConta,
        });
        const newId = newUser?.id;
        if (newId) {
          const extras = userContaIds.filter(id => id !== primaryConta);
          // Garante que o user pode ser linkado em N contas (default é 1)
          if (extras.length > 0) {
            await api.put(`/users/${newId}`, { maxCompanies: userContaIds.length });
            await Promise.all(extras.map(id => api.post(`/users/${newId}/companies`, { companyId: id })));
          }
        }
        toast.success("Usuário criado");
      }
      setEditingUser(null);
      setUserContaIds([]);
      await fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingUser(false);
    }
  };

  // Delete agora roda via UserDeleteImpactModal (preview + plano de
  // transferência por company). O modal faz GET delete-impact e DELETE
  // com body { transfers }; aqui só refetchamos a lista no callback.
  const handleUserDeleted = async () => {
    setDeletingUser(null);
    await fetchData();
  };

  const handleResetMfa = async () => {
    if (!editingUser?.id || !editingUser.mfaEnabled) return;
    if (!window.confirm("Tem certeza? O usuário precisará reconfigurar o app no próximo login.")) return;
    setResettingMfa(true);
    try {
      await api.post(`/users/${editingUser.id}/mfa/reset`);
      toast.success("MFA resetado");
      setEditingUser({ ...editingUser, mfaEnabled: false, mfaEnabledAt: null });
      await fetchData();
    } catch (err) {
      toastError(err);
    } finally {
      setResettingMfa(false);
    }
  };

  // ============== EMPRESA ==============
  const handleSaveEmpresa = async () => {
    if (!creatingEmpresa?.name?.trim()) return;
    try {
      await api.post("/companies", {
        name: creatingEmpresa.name,
        groupName: creatingEmpresa.name,
        isInternalChatChannel: true,
      });
      toast.success("Empresa criada");
      setCreatingEmpresa(null);
      await fetchData();
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDetail = (empresa) => {
    setSelectedGroupName(empresa.groupName);
    setView("detail");
  };

  const handleBack = () => {
    setView("list");
    setSelectedGroupName(null);
  };

  // ============== RENDER HELPERS ==============
  const renderEditContaModal = () => {
    if (!editingConta) return null;
    return (
      <div className={classes.modalBackdrop} onClick={() => setEditingConta(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>{editingConta._isNew ? "Adicionar Conta" : "Editar Conta"}</div>
            <button className={classes.iconBtn} onClick={() => setEditingConta(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Nome da Conta</label>
              <input
                className={classes.fieldInput}
                value={editingConta.name || ""}
                onChange={(e) => setEditingConta({ ...editingConta, name: e.target.value })}
                placeholder="Ex: Empresa 1"
                autoFocus
              />
              <div className={classes.fieldHint}>Limites de usuários são configurados na Empresa, em "Editar Empresa". Esta Conta herdará os limites do grupo automaticamente.</div>
            </div>

            {/* Dados Fiscais POR CONTA (cada loja tem CNPJ próprio) */}
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Razão Social</label>
                <input className={classes.fieldInput} value={editingConta.razaoSocial || ""}
                  onChange={(e) => setEditingConta({ ...editingConta, razaoSocial: e.target.value })}
                  placeholder="Empresa LTDA" autoComplete="off" />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Nome Fantasia</label>
                <input className={classes.fieldInput} value={editingConta.nomeFantasia || ""}
                  onChange={(e) => setEditingConta({ ...editingConta, nomeFantasia: e.target.value })}
                  placeholder="Nome comercial" autoComplete="off" />
              </div>
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>CNPJ</label>
                <input className={classes.fieldInput} value={maskCnpj(editingConta.cnpj || "")}
                  onChange={(e) => setEditingConta({ ...editingConta, cnpj: e.target.value.replace(/\D/g, "").slice(0, 14) })}
                  placeholder="00.000.000/0000-00" autoComplete="off" />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Telefone</label>
                <input className={classes.fieldInput} value={editingConta.phone || ""}
                  onChange={(e) => setEditingConta({ ...editingConta, phone: e.target.value })}
                  placeholder="(11) 99999-9999" autoComplete="off" />
              </div>
            </div>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>E-mail de cobrança</label>
              <input type="text" inputMode="email" className={classes.fieldInput} value={editingConta.email || ""}
                onChange={(e) => setEditingConta({ ...editingConta, email: e.target.value })}
                placeholder="financeiro@empresa.com" autoComplete="off" />
              <div className={classes.fieldHint}>O CNPJ e o e-mail da <strong>conta principal de cobrança</strong> (definida em "Editar Empresa") alimentam a Pagar.me e o pedido no painel.</div>
            </div>

            <div className={classes.field}>
              <label className={classes.fieldLabel}>API Key da OpenAI (IA grátis do teste)</label>
              <input
                className={classes.fieldInput}
                type="password"
                autoComplete="new-password"
                value={aiKey}
                onChange={(e) => setAiKey(e.target.value)}
                placeholder={aiKeyMeta.hasKey ? `Chave atual: ${aiKeyMeta.apiKeyMask}` : "sk-..."}
              />
              <div className={classes.fieldHint}>
                Libera a IA desta conta durante o teste grátis. Não aparece em "Integrações" e é desativada automaticamente quando o teste acaba (depois o cliente cadastra a própria chave).
                {aiKeyMeta.loading && <> · carregando…</>}
                {!aiKeyMeta.loading && aiKeyMeta.hasKey && (
                  <>
                    {" · "}Atual: {aiKeyMeta.apiKeyMask} ({aiKeyMeta.isActive ? "ativa" : "inativa"}){" "}
                    <button
                      type="button"
                      onClick={handleRemoveAiKey}
                      style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: 0, textDecoration: "underline", font: "inherit" }}
                    >
                      Remover
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setEditingConta(null)} disabled={savingConta}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={handleSaveConta} disabled={savingConta || !editingConta.name?.trim()}>
              {savingConta ? "Salvando..." : (editingConta._isNew ? "Criar" : "Salvar")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditUserModal = () => {
    if (!editingUser) return null;
    const groupContas = companies.filter(c => (c.groupName || null) === selectedGroupName);
    return (
      <div className={classes.modalBackdrop} onClick={() => setEditingUser(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>{editingUser._isNew ? "Adicionar Usuário" : "Editar Usuário"}</div>
            <button className={classes.iconBtn} onClick={() => setEditingUser(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Nome</label>
                <input className={classes.fieldInput} value={editingUser.name || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  placeholder="João Silva" autoFocus autoComplete="off" />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Email</label>
                <input type="text" inputMode="email" className={classes.fieldInput}
                  value={editingUser.email || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                  placeholder="joao@empresa.com" autoComplete="off" />
              </div>
            </div>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>{editingUser._isNew ? "Senha (mín 6 caracteres)" : "Nova senha (deixe vazio para não alterar)"}</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className={classes.fieldInput}
                  style={{ paddingRight: 40 }}
                  value={editingUser.password || ""}
                  onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                  placeholder={editingUser._isNew ? "Obrigatório" : "••••••"}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  title={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  style={{
                    position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                    background: "transparent", border: "none", cursor: "pointer",
                    color: dark ? "#A1A1AA" : "#71717A",
                    padding: 6, display: "flex", alignItems: "center", borderRadius: 6,
                  }}
                >
                  {showPassword ? <EyeOff size={16} strokeWidth={1.75} /> : <Eye size={16} strokeWidth={1.75} />}
                </button>
              </div>
            </div>
            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Perfil</label>
                <SingleSelect
                  classes={classes}
                  dark={dark}
                  value={editingUser.profile || "user"}
                  onChange={(v) => setEditingUser({ ...editingUser, profile: v })}
                  options={[
                    { value: "user", label: "Usuário" },
                    { value: "admin", label: "Admin" },
                  ]}
                />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Tipo de acesso</label>
                <SingleSelect
                  classes={classes}
                  dark={dark}
                  value={editingUser.accessScope || "full"}
                  onChange={(v) => setEditingUser({ ...editingUser, accessScope: v })}
                  options={ACCESS_SCOPES}
                  renderOption={(opt) => (
                    <>
                      <opt.Icon size={14} strokeWidth={2} style={{ color: opt.color, flexShrink: 0 }} />
                      <span>{opt.label}</span>
                    </>
                  )}
                />
              </div>
            </div>
            {!editingUser._isNew && (currentUser?.super || currentUser?.profile === "admin") && (
              <div className={classes.mfaCard}>
                <div className={classes.mfaCardLeft}>
                  <div className={`${classes.mfaIconBadge} ${editingUser.mfaEnabled ? classes.mfaIconBadgeOn : classes.mfaIconBadgeOff}`}>
                    {editingUser.mfaEnabled
                      ? <ShieldCheck size={18} strokeWidth={1.75} />
                      : <ShieldOff size={18} strokeWidth={1.75} />}
                  </div>
                  <div className={classes.mfaTextWrap}>
                    <span className={classes.mfaTitle}>Autenticação em duas etapas</span>
                    <span className={`${classes.mfaStatus} ${editingUser.mfaEnabled ? classes.mfaStatusOn : classes.mfaStatusOff}`}>
                      {editingUser.mfaEnabled
                        ? (editingUser.mfaEnabledAt
                            ? `Ativa desde ${new Date(editingUser.mfaEnabledAt).toLocaleDateString()}`
                            : "Ativa")
                        : "Não configurada"}
                    </span>
                  </div>
                </div>
                {editingUser.mfaEnabled && (
                  <button
                    type="button"
                    className={classes.mfaResetBtn}
                    onClick={handleResetMfa}
                    disabled={resettingMfa}
                  >
                    <RotateCcw size={13} strokeWidth={2} />
                    {resettingMfa ? "Resetando..." : "Resetar MFA"}
                  </button>
                )}
              </div>
            )}
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Contas com acesso</label>
              <div className={classes.multiSelectWrap} ref={multiSelectRef}>
                <button
                  type="button"
                  className={classes.multiSelectTrigger}
                  onClick={() => setMultiSelectOpen(o => !o)}
                >
                  <span className={classes.multiSelectLabel}>
                    {userContaIds.length === 0
                      ? "Selecione as contas..."
                      : userContaIds.length === 1
                        ? groupContas.find(c => c.id === userContaIds[0])?.name || "1 conta"
                        : `${userContaIds.length} contas selecionadas`}
                  </span>
                  <ChevronDown size={16} style={{ transform: multiSelectOpen ? "rotate(180deg)" : "none", transition: "transform 0.15s" }} />
                </button>
                {multiSelectOpen && (
                  <div className={classes.multiSelectMenu}>
                    {groupContas.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 12, color: dark ? "#71717A" : "#A1A1AA", textAlign: "center" }}>Nenhuma conta nesta empresa</div>
                    ) : groupContas.map(conta => {
                      const checked = userContaIds.includes(conta.id);
                      const isPrimary = conta.id === editingUser.companyId;
                      return (
                        <div key={conta.id} className={classes.multiSelectItem}
                          onClick={() => {
                            // Sincronia: ao desmarcar a Conta PRINCIPAL, auto-promove
                            // a próxima Conta vinculada (ou null se for a última).
                            // Sem isto, ficavam estados inconsistentes: select com
                            // value inválido + save bloqueado pela validação nova.
                            if (checked) {
                              const next = userContaIds.filter(id => id !== conta.id);
                              setUserContaIds(next);
                              if (editingUser.companyId === conta.id) {
                                setEditingUser({ ...editingUser, companyId: next[0] || null });
                              }
                            } else {
                              setUserContaIds([...userContaIds, conta.id]);
                            }
                          }}>
                          <input type="checkbox" checked={checked} readOnly
                            style={{ accentColor: dark ? "#5B7CFF" : "#3D5CFD", width: 15, height: 15, pointerEvents: "none" }} />
                          <div style={{ flex: 1, fontSize: 13, color: dark ? "#FAFAFA" : "#09090B", fontWeight: 500 }}>{conta.name}</div>
                          {isPrimary && <span style={{ fontSize: 10, fontWeight: 700, color: dark ? "#5B7CFF" : "#3D5CFD" }}>PRINCIPAL</span>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className={classes.fieldHint}>O usuário pode trocar entre as Contas selecionadas no perfil. A "principal" é onde ele entra por padrão.</div>
            </div>
            {userContaIds.length >= 1 && (
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Conta principal</label>
                <SingleSelect
                  classes={classes}
                  dark={dark}
                  // value reflete EXATAMENTE editingUser.companyId — sem fallback
                  // pra primeira vinculada. Se ficar fora das options (ex.: user
                  // desmarcou a primary), o onChange do checkbox já reajusta.
                  value={editingUser.companyId}
                  onChange={(v) => setEditingUser({ ...editingUser, companyId: v })}
                  options={userContaIds.map((id) => {
                    const conta = groupContas.find((c) => c.id === id);
                    return { value: id, label: conta?.name || String(id) };
                  })}
                />
                <div className={classes.fieldHint}>Conta onde o usuário entra por padrão ao logar. Pode trocar entre as outras pelo perfil.</div>
              </div>
            )}
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setEditingUser(null)} disabled={savingUser}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={handleSaveUser} disabled={savingUser || !editingUser.name?.trim() || !editingUser.email?.trim()}>
              {savingUser ? "Salvando..." : (editingUser._isNew ? "Criar" : "Salvar")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderCreateEmpresaModal = () => {
    if (!creatingEmpresa) return null;
    return (
      <div className={classes.modalBackdrop} onClick={() => setCreatingEmpresa(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>Criar Empresa</div>
            <button className={classes.iconBtn} onClick={() => setCreatingEmpresa(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Nome da Empresa</label>
              <input className={classes.fieldInput} value={creatingEmpresa.name || ""}
                onChange={(e) => setCreatingEmpresa({ ...creatingEmpresa, name: e.target.value })}
                placeholder="Ex: Up&Help" autoFocus />
              <div className={classes.fieldHint}>Uma Empresa agrupa várias Contas. Será criada uma Conta inicial com o mesmo nome.</div>
            </div>
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setCreatingEmpresa(null)}>Cancelar</button>
            <button className={classes.primaryBtn} onClick={handleSaveEmpresa} disabled={!creatingEmpresa.name?.trim()}>Criar</button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditEmpresaModal = () => {
    if (!editingEmpresa) return null;
    const parseLimit = (v) => v === "" || v === null || v === undefined ? null : Math.max(0, parseInt(v, 10) || 0);
    return (
      <div className={classes.modalBackdrop} onClick={() => !savingEmpresa && setEditingEmpresa(null)}>
        <div className={classes.modalCard} onClick={(e) => e.stopPropagation()}>
          <div className={classes.modalHeader}>
            <div className={classes.modalTitle}>Editar Empresa</div>
            <button className={classes.iconBtn} onClick={() => !savingEmpresa && setEditingEmpresa(null)} title="Fechar"><X size={18} /></button>
          </div>
          <div className={classes.modalBody}>
            <div className={classes.field}>
              <label className={classes.fieldLabel}>Nome da Empresa</label>
              <input
                className={classes.fieldInput}
                value={editingEmpresa.name || ""}
                onChange={(e) => setEditingEmpresa({ ...editingEmpresa, name: e.target.value })}
                placeholder="Ex: Up&Help"
                autoFocus
              />
              <div className={classes.fieldHint}>
                O nome é aplicado a todas as Contas vinculadas a essa Empresa.
              </div>
            </div>

            <div className={classes.field}>
              <label className={classes.fieldLabel}>Limite de Contas (WhatsApp + Chat Interno)</label>
              <input
                type="number"
                min={0}
                className={classes.fieldInput}
                value={editingEmpresa.maxAccountsInGroup ?? ""}
                onChange={(e) => setEditingEmpresa({ ...editingEmpresa, maxAccountsInGroup: parseLimit(e.target.value) })}
                placeholder="Vazio = ∞"
              />
              <div className={classes.fieldHint}>
                Bloqueia criação de novas Contas dentro desta Empresa quando o teto é atingido.
              </div>
            </div>

            <div className={classes.field}>
              <label className={classes.fieldLabel}>Limite Total de Usuários</label>
              <input
                type="number"
                min={0}
                className={classes.fieldInput}
                value={editingEmpresa.maxTotalUsersInGroup ?? ""}
                onChange={(e) => setEditingEmpresa({ ...editingEmpresa, maxTotalUsersInGroup: parseLimit(e.target.value) })}
                placeholder="Vazio = ∞"
              />
              <div className={classes.fieldHint}>
                Bloqueia criação de qualquer novo usuário quando o teto total é atingido (independente do escopo).
              </div>
            </div>

            <div className={classes.fieldRow}>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Limite Chat Interno</label>
                <input
                  type="number"
                  min={0}
                  className={classes.fieldInput}
                  value={editingEmpresa.maxInternalChatUsersInGroup ?? ""}
                  onChange={(e) => setEditingEmpresa({ ...editingEmpresa, maxInternalChatUsersInGroup: parseLimit(e.target.value) })}
                  placeholder="Vazio = ∞"
                />
              </div>
              <div className={classes.field}>
                <label className={classes.fieldLabel}>Limite Chat Atendimento</label>
                <input
                  type="number"
                  min={0}
                  className={classes.fieldInput}
                  value={editingEmpresa.maxAttendanceUsersInGroup ?? ""}
                  onChange={(e) => setEditingEmpresa({ ...editingEmpresa, maxAttendanceUsersInGroup: parseLimit(e.target.value) })}
                  placeholder="Vazio = ∞"
                />
              </div>
            </div>
            <div className={classes.fieldHint}>
              Limites por escopo contam usuários cujo accessScope inclui o canal. "Completo" entra nas duas contagens (interno + atendimento). Multi-conta não consome cobrança extra.
            </div>

            <div className={classes.field} style={{ marginTop: 8, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <label className={classes.fieldLabel} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!editingEmpresa.billingExemptInGroup}
                  onChange={(e) => setEditingEmpresa({ ...editingEmpresa, billingExemptInGroup: e.target.checked })}
                  style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#3D5CFD" }}
                />
                Conta cortesia (isenta de cobrança)
              </label>
              <div className={classes.fieldHint}>
                Quando marcado, esta Empresa e todas as suas Contas usam o sistema <strong>gratuitamente</strong> — saem completamente do fluxo de pagamento (sem teste, sem plano, sem paywall e sem teto de assentos).
              </div>
            </div>

            <div className={classes.field} style={{ marginTop: 8, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              <label className={classes.fieldLabel} style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!editingEmpresa.onboardingDisabled}
                  onChange={(e) => setEditingEmpresa({ ...editingEmpresa, onboardingDisabled: e.target.checked })}
                  style={{ width: 18, height: 18, cursor: "pointer", accentColor: "#3D5CFD" }}
                />
                Desabilitar onboarding
              </label>
              <div className={classes.fieldHint}>
                Quando marcado, o fluxo de onboarding guiado (checklist, tour, banner de trial e launcher) não aparece pra nenhum admin desta Empresa.
              </div>
            </div>

            {/* Conta principal de cobrança: define qual Conta do grupo fornece
                CNPJ/e-mail à Pagar.me/pedido do painel (1 por Empresa). */}
            {(() => {
              const groupContas = companies.filter(
                c => (c.groupName || null) === editingEmpresa._originalGroupName
              );
              if (groupContas.length === 0) return null;
              return (
                <div className={classes.field} style={{ marginTop: 8, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                  <label className={classes.fieldLabel}>Conta principal de cobrança</label>
                  <select
                    className={classes.fieldInput}
                    value={editingEmpresa.primaryBillingCompanyId || ""}
                    onChange={(e) => setEditingEmpresa({ ...editingEmpresa, primaryBillingCompanyId: Number(e.target.value) })}
                  >
                    {groupContas.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <div className={classes.fieldHint}>
                    É o <strong>CNPJ e o e-mail</strong> desta Conta que alimentam a Pagar.me e o pedido no painel de cobrança.
                  </div>
                </div>
              );
            })()}
          </div>
          <div className={classes.modalActions}>
            <button className={classes.ghostBtn} onClick={() => setEditingEmpresa(null)} disabled={savingEmpresa}>Cancelar</button>
            <button
              className={classes.primaryBtn}
              onClick={handleSaveEditEmpresa}
              disabled={savingEmpresa || !editingEmpresa.name?.trim()}
            >
              {savingEmpresa ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ============== DETAIL VIEW ==============
  if (view === "detail" && selectedEmpresa) {
    const groupContas = selectedEmpresa.contas;
    const groupContaIds = new Set(groupContas.map(c => c.id));
    const groupUsers = users.filter(u => groupContaIds.has(u.companyId));
    const createdAtFmt = selectedEmpresa.earliestCreatedAt
      ? selectedEmpresa.earliestCreatedAt.toLocaleDateString("pt-BR")
      : null;

    // Busca + paginação Contas
    const contasFiltered = contasSearch.trim()
      ? groupContas.filter(c => c.name?.toLowerCase().includes(contasSearch.toLowerCase()))
      : groupContas;
    const contasTotalPages = totalPages(contasFiltered.length, CONTAS_PER_PAGE);
    const contasPageSafe = Math.min(contasPage, contasTotalPages);
    const contasPaginated = paginate(contasFiltered, contasPageSafe, CONTAS_PER_PAGE);
    // Pré-calcula contagem de users por Conta — evita O(contas × users) ao
    // renderizar a grid (cada Conta fazia users.filter no map).
    const userCountByContaId = groupUsers.reduce((acc, u) => {
      acc[u.companyId] = (acc[u.companyId] || 0) + 1;
      return acc;
    }, {});

    // Busca + paginação Usuários
    const usersFiltered = usersSearch.trim()
      ? groupUsers.filter(u =>
          u.name?.toLowerCase().includes(usersSearch.toLowerCase()) ||
          u.email?.toLowerCase().includes(usersSearch.toLowerCase())
        )
      : groupUsers;
    const usersTotalPages = totalPages(usersFiltered.length, USERS_PER_PAGE);
    const usersPageSafe = Math.min(usersPage, usersTotalPages);
    const usersPaginated = paginate(usersFiltered, usersPageSafe, USERS_PER_PAGE);

    return (
      <div className={classes.root}>
        {/* Header: voltar + nome + criada em + Editar/Excluir */}
        <div className={classes.detailHeaderV2}>
          <button
            className={classes.detailBackBtn}
            onClick={handleBack}
            title="Voltar"
            aria-label="Voltar"
          >
            <ChevronRight size={20} style={{ transform: "rotate(180deg)" }} />
          </button>
          <div className={classes.detailTitleWrap}>
            <div className={classes.detailNameV2}>{selectedEmpresa.displayName}</div>
            {createdAtFmt && (
              <div className={classes.detailSubV2}>Criada em {createdAtFmt}</div>
            )}
          </div>
          <div className={classes.detailActionsV2}>
            <button
              className={classes.detailEditBtn}
              onClick={() => {
                // Limites são denormalizados nas Contas — pega da primeira.
                const ref = selectedEmpresa.contas[0] || {};
                // Conta principal de cobrança: a marcada, ou fallback à 1ª.
                const primaryConta =
                  selectedEmpresa.contas.find(c => c.isBillingPrimaryInGroup) || ref;
                setEditingEmpresa({
                  name: selectedEmpresa.displayName,
                  _originalGroupName: selectedEmpresa.groupName,
                  maxAccountsInGroup: ref.maxAccountsInGroup ?? null,
                  maxInternalChatUsersInGroup: ref.maxInternalChatUsersInGroup ?? null,
                  maxAttendanceUsersInGroup: ref.maxAttendanceUsersInGroup ?? null,
                  maxTotalUsersInGroup: ref.maxTotalUsersInGroup ?? null,
                  billingExemptInGroup: !!ref.billingExemptInGroup,
                  onboardingDisabled: !!ref.onboardingDisabled,
                  primaryBillingCompanyId: primaryConta?.id ?? null,
                });
              }}
            >
              Editar
            </button>
            {!selectedEmpresa.isSystem && (
              <button
                className={classes.detailDeleteBtn}
                onClick={() => setDeletingEmpresa(selectedEmpresa)}
              >
                Excluir
              </button>
            )}
          </div>
        </div>

        {/* KPI Cards: Total de Usuários / Contas / Chat Atendimento / Chat Interno */}
        {(() => {
          const u = groupsUsage[selectedEmpresa.groupName || ""] || null;
          const kpis = [
            {
              label: "Total de Usuários",
              used: u?.total?.used ?? 0,
              limit: u?.total?.limit ?? null,
              Icon: Users,
              color: "#5B7CFF",
              tooltip: "Total de usuários cuja Conta primária pertence a esta Empresa. Cada usuário é contado uma única vez, mesmo que tenha acesso a múltiplas Contas (multi-conta não cobra licença extra). O limite é configurado em \"Editar Empresa\".",
            },
            {
              label: "Contas",
              used: u?.contas?.used ?? groupContas.length,
              limit: u?.contas?.limit ?? null,
              Icon: Building2,
              color: "#8B5CF6",
              tooltip: "Contas (WhatsApp + Chat Interno) ativas nesta Empresa. O limite é configurado em \"Editar Empresa\". Quando atingido, novas Contas ficam bloqueadas.",
            },
            {
              label: "Chat Atendimento",
              used: u?.atendimento?.used ?? 0,
              limit: u?.atendimento?.limit ?? null,
              Icon: Headphones,
              color: "#F59E0B",
              tooltip: "Usuários cujo escopo de acesso inclui Chat de Atendimento (escopo \"Atendimento\" ou \"Completo\"). O limite é configurado em \"Editar Empresa\". Quando atingido, novos usuários de atendimento ficam bloqueados.",
            },
            {
              label: "Chat Interno",
              used: u?.interno?.used ?? 0,
              limit: u?.interno?.limit ?? null,
              Icon: MessageCircle,
              color: "#10B981",
              tooltip: "Usuários cujo escopo de acesso inclui Chat Interno (escopo \"Interno\" ou \"Completo\"). O limite é configurado em \"Editar Empresa\". Quando atingido, novos usuários internos ficam bloqueados.",
            },
          ];
          return (
            <div className={classes.kpiGrid}>
              {kpis.map((k, idx) => {
                const hasLimit = k.limit != null;
                const pct = hasLimit && k.limit > 0 ? Math.min(100, Math.round((k.used / k.limit) * 100)) : 0;
                const warn = hasLimit && pct >= 90;
                const Icon = k.Icon;
                return (
                  <div key={idx} className={classes.kpiCard}>
                    <div className={classes.kpiCardHeader}>
                      <div
                        className={classes.kpiIconChip}
                        style={{
                          background: `${k.color}1F`,
                          color: k.color,
                          border: `1px solid ${k.color}33`,
                        }}
                      >
                        <Icon size={16} strokeWidth={2} />
                      </div>
                      <div className={classes.kpiLabel}>{k.label}</div>
                      <Tooltip title={k.tooltip} arrow placement="top">
                        <span className={classes.kpiInfoBtn} tabIndex={0} aria-label={`Sobre ${k.label}`}>
                          <Info size={13} strokeWidth={2} />
                        </span>
                      </Tooltip>
                    </div>
                    <div className={classes.kpiValueRow}>
                      <div className={classes.kpiValue}>{k.used}</div>
                      {hasLimit ? (
                        <div className={classes.kpiValueSub}>de {k.limit}</div>
                      ) : null}
                    </div>
                    {hasLimit ? (
                      <div className={classes.kpiOverBar} aria-hidden="true">
                        <div
                          className={`${classes.kpiOverFill} ${warn ? classes.kpiOverFillWarn : ""}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    ) : (
                      <div className={classes.kpiUnlimited}>Sem limite</div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Seção: Contas */}
        <div className={classes.sectionV2}>
          <div className={classes.sectionHeaderV2}>
            <div className={classes.sectionTitleWrapV2}>
              <span className={classes.sectionTitleV2}>Contas</span>
              <span className={classes.sectionCountV2}>
                {contasFiltered.length} de {groupContas.length}
              </span>
            </div>
            <div className={classes.sectionActionsV2}>
              <button className={classes.primaryBtn} onClick={handleOpenCreateConta}>
                <Plus size={16} /> Adicionar Conta
              </button>
            </div>
          </div>

          <div className={classes.searchWrap}>
            <span className={classes.searchIcon}>
              <Search size={16} />
            </span>
            <input
              type="search"
              autoComplete="off"
              className={classes.searchInput}
              placeholder="Buscar conta por nome..."
              value={contasSearch}
              onChange={(e) => setContasSearch(e.target.value)}
            />
          </div>

          {contasFiltered.length === 0 ? (
            <div className={classes.emptyState}>
              {contasSearch ? "Nenhuma conta encontrada" : "Nenhuma conta nesta empresa"}
            </div>
          ) : (
            <>
              <div className={classes.contasGrid}>
                {contasPaginated.map(conta => {
                  const contaUserCount = userCountByContaId[conta.id] || 0;
                  return (
                    <div key={conta.id} className={classes.contaCard}>
                      <div className={classes.contaName}>
                        <Building2 size={16} color={dark ? "#5B7CFF" : "#3D5CFD"} />
                        {conta.name}
                      </div>
                      <div className={classes.contaStats}>
                        <span><Users size={11} style={{ verticalAlign: "middle" }} /> {contaUserCount} {contaUserCount === 1 ? "usuário" : "usuários"}</span>
                      </div>
                      <div className={classes.contaActions}>
                        {canImpersonate && (
                          <Tooltip title={`Acessar Conta "${conta.name}"`} arrow placement="top">
                            <button
                              type="button"
                              className={classes.iconBtn}
                              onClick={async () => {
                                if (switchingContaId) return;
                                setSwitchingContaId(conta.id);
                                await superSwitchCompany(conta.id);
                              }}
                              disabled={switchingContaId === conta.id}
                              aria-label={`Acessar conta ${conta.name}`}
                            >
                              <LogIn size={14} strokeWidth={2} />
                            </button>
                          </Tooltip>
                        )}
                        <button className={classes.iconBtn} onClick={() => handleOpenEditConta(conta)} title="Editar"><Pencil size={14} /></button>
                        <button className={`${classes.iconBtn} ${classes.danger}`} onClick={() => setDeletingConta(conta)} title="Excluir"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {renderPagination(contasPageSafe, contasTotalPages, setContasPage)}
            </>
          )}
        </div>

        {/* Seção: Usuários (tabela densa) */}
        <div className={classes.sectionV2}>
          <div className={classes.sectionHeaderV2}>
            <div className={classes.sectionTitleWrapV2}>
              <span className={classes.sectionTitleV2}>Usuários</span>
              <span className={classes.sectionCountV2}>
                {usersFiltered.length} de {groupUsers.length}
              </span>
            </div>
            <div className={classes.sectionActionsV2}>
              <button className={classes.primaryBtn} onClick={handleOpenCreateUser}>
                <Plus size={16} /> Adicionar Usuário
              </button>
            </div>
          </div>

          <div className={classes.searchWrap}>
            <span className={classes.searchIcon}>
              <Search size={16} />
            </span>
            <input
              type="search"
              autoComplete="off"
              className={classes.searchInput}
              placeholder="Buscar usuário por nome ou email..."
              value={usersSearch}
              onChange={(e) => setUsersSearch(e.target.value)}
            />
          </div>

          <UsersTable
            users={usersPaginated}
            currentUserId={currentUser?.id}
            getCompanyName={(u) => groupContas.find(c => c.id === u.companyId)?.name || null}
            canImpersonate={canImpersonate}
            impersonatingId={impersonatingId}
            onImpersonate={async (u) => {
              if (impersonatingId) return;
              setImpersonatingId(u.id);
              await impersonateUser(u.id);
            }}
            onEdit={handleOpenEditUser}
            onDelete={(u) => setDeletingUser(u)}
            searchActive={!!usersSearch}
            emptyMessage="Nenhum usuário nesta empresa"
            emptySearchMessage="Nenhum usuário encontrado"
          />
          {renderPagination(usersPageSafe, usersTotalPages, setUsersPage)}
        </div>

        <div className={classes.bottomSpacer} aria-hidden="true" />

        {renderEditContaModal()}
        {renderEditUserModal()}
        {renderEditEmpresaModal()}
        <ContaSelectorModal
          open={!!contaPickerEmpresa}
          empresa={contaPickerEmpresa}
          onClose={() => setContaPickerEmpresa(null)}
          onSelect={async (conta) => {
            setSwitchingEmpresaKey(contaPickerEmpresa?.displayName || "");
            await superSwitchCompany(conta.id);
            // superSwitchCompany faz window.location.assign → não precisa
            // limpar contaPickerEmpresa nem switchingEmpresaKey.
          }}
        />
        <ConfirmationModal
          open={!!deletingConta}
          title={deletingConta ? `Excluir "${deletingConta.name}"?` : ""}
          onClose={() => setDeletingConta(null)}
          onConfirm={handleDeleteConta}
        >
          Essa ação não pode ser desfeita. Usuários, mensagens e dados ligados a essa Conta podem ser afetados.
        </ConfirmationModal>
        <UserDeleteImpactModal
          open={!!deletingUser}
          userId={deletingUser?.id}
          userName={deletingUser?.name}
          onClose={() => setDeletingUser(null)}
          onDeleted={handleUserDeleted}
        />
        <ConfirmationModal
          open={!!deletingEmpresa}
          title={deletingEmpresa ? `Excluir empresa "${deletingEmpresa.displayName}"?` : ""}
          onClose={() => setDeletingEmpresa(null)}
          onConfirm={handleDeleteEmpresa}
          confirmLoading={deletingEmpresaBusy}
        >
          {deletingEmpresa && `Todas as ${deletingEmpresa.contas.length} contas desta empresa e os dados vinculados (usuários, mensagens, tickets, conexões WhatsApp) serão removidos. Essa ação NÃO pode ser desfeita.`}
        </ConfirmationModal>
      </div>
    );
  }

  // ============== LIST VIEW ==============
  return (
    <div className={classes.root}>
      <div className={classes.listHeader}>
        <div>
          <div className={classes.listTitle}>Empresas</div>
          <div className={classes.listSub}>{empresas.length} {empresas.length === 1 ? "empresa cadastrada" : "empresas cadastradas"}</div>
        </div>
        <button className={classes.primaryBtn} onClick={() => setCreatingEmpresa({ name: "" })}>
          <Plus size={16} /> Criar Empresa
        </button>
      </div>

      {loading ? (
        <div className={classes.cardsGrid} aria-busy="true" aria-live="polite">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className={classes.skeletonCard}>
              <div className={classes.skeletonRow}>
                <div className={classes.skeletonBlock} style={{ width: 40, height: 40, borderRadius: 10 }} />
                <div style={{ flex: 1 }}>
                  <div className={classes.skeletonBlock} style={{ width: "60%", height: 14, marginBottom: 8 }} />
                  <div className={classes.skeletonBlock} style={{ width: "40%", height: 11 }} />
                </div>
              </div>
              <div className={classes.skeletonFooter}>
                <div>
                  <div className={classes.skeletonBlock} style={{ width: 32, height: 18, margin: "0 auto 6px" }} />
                  <div className={classes.skeletonBlock} style={{ width: 48, height: 9, margin: "0 auto" }} />
                </div>
                <div>
                  <div className={classes.skeletonBlock} style={{ width: 32, height: 18, margin: "0 auto 6px" }} />
                  <div className={classes.skeletonBlock} style={{ width: 48, height: 9, margin: "0 auto" }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : empresas.length === 0 ? (
        <div className={classes.emptyHero}>
          <div className={classes.emptyHeroIcon}>
            <Building2 size={32} strokeWidth={1.6} />
          </div>
          <div className={classes.emptyHeroTitle}>Nenhuma empresa cadastrada</div>
          <div className={classes.emptyHeroSub}>
            Crie a primeira empresa para começar a organizar contas e usuários do seu grupo. Você poderá adicionar quantas contas precisar e gerenciar permissões individualmente.
          </div>
          <button
            className={`${classes.primaryBtn} ${classes.emptyHeroCta}`}
            onClick={() => setCreatingEmpresa({ name: "" })}
          >
            <Plus size={16} /> Criar primeira empresa
          </button>
        </div>
      ) : (
        <>
          <div className={classes.trackingGrid}>
            {paginate(empresas, Math.min(empresasPage, totalPages(empresas.length, EMPRESAS_PER_PAGE)), EMPRESAS_PER_PAGE).map(emp => (
              <div
                key={emp.displayName}
                className={classes.trackingCard}
                onClick={() => handleOpenDetail(emp)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") handleOpenDetail(emp); }}
              >
                <div className={classes.trackingCardHeader}>
                  <div className={classes.trackingIconBox}>
                    <Building2 size={24} strokeWidth={1.8} />
                  </div>
                  <div className={classes.trackingCardTitle}>
                    <div className={classes.trackingCardName}>{emp.displayName}</div>
                    {emp.isSystem && (
                      <span className={classes.trackingSistemaBadge}>Sistema</span>
                    )}
                  </div>
                </div>
                {(() => {
                  const u = groupsUsage[emp.groupName || ""] || null;
                  const usersUsed = u?.total?.used ?? 0;
                  const usersLimit = u?.total?.limit ?? null;
                  const contasUsed = u?.contas?.used ?? emp.contas.length;
                  const contasLimit = u?.contas?.limit ?? null;
                  const fmt = (used, limit) =>
                    limit != null ? `${used} / ${limit}` : `${used}`;
                  return (
                    <div className={classes.trackingStatsRow}>
                      <div className={classes.trackingStatBox}>
                        <div className={classes.trackingStatValue}>{fmt(usersUsed, usersLimit)}</div>
                        <div className={classes.trackingStatLabel}>Usuários</div>
                      </div>
                      <div className={classes.trackingStatBox}>
                        <div className={classes.trackingStatValue}>{fmt(contasUsed, contasLimit)}</div>
                        <div className={classes.trackingStatLabel}>Contas</div>
                      </div>
                    </div>
                  );
                })()}
                {(() => {
                  // "Sistema" não tem Acessar Conta (o super já está nela).
                  const showAccessConta = canImpersonate && !emp.isSystem && emp.contas?.length > 0;
                  // Esta Empresa é a que o user está acessando agora?
                  // Compara pelo groupName atual do user (vem do JWT/SerializeUser).
                  const activeGroupName = currentUser?.company?.groupName || null;
                  const isActiveEmpresa = showAccessConta && activeGroupName === emp.groupName;
                  const activeContaId = isActiveEmpresa ? currentUser?.companyId : null;
                  const activeConta = activeContaId
                    ? emp.contas.find(c => c.id === activeContaId)
                    : null;
                  const canSwitchContaHere = isActiveEmpresa && emp.contas.length > 1;
                  const rowCls = showAccessConta ? classes.trackingActionsRow : classes.trackingActionsRowSingle;
                  return (
                    <div className={rowCls}>
                      {showAccessConta && (isActiveEmpresa ? (
                        // Estado "Conta ativa": feedback verde + nome da Conta atual.
                        // Multi-conta vira clicável (abre seletor pra trocar).
                        <button
                          type="button"
                          className={`${classes.trackingActiveBtn} ${canSwitchContaHere ? classes.trackingActiveBtnClickable : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canSwitchContaHere) return;
                            setContaPickerEmpresa(emp);
                          }}
                          aria-label={
                            canSwitchContaHere
                              ? `Trocar conta (atual: ${activeConta?.name || ""})`
                              : `Você está acessando ${activeConta?.name || emp.displayName}`
                          }
                          title={
                            canSwitchContaHere
                              ? `Conta atual: ${activeConta?.name || "—"} · clique para trocar`
                              : "Você está acessando esta conta"
                          }
                          tabIndex={canSwitchContaHere ? 0 : -1}
                        >
                          <span className={classes.trackingActiveDot} aria-hidden="true" />
                          <Check size={14} strokeWidth={2.5} />
                          <span className={classes.trackingBtnLabel}>
                            {canSwitchContaHere ? "Trocar conta" : "Conta ativa"}
                          </span>
                        </button>
                      ) : (
                        <button
                          type="button"
                          className={classes.trackingAccessBtn}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (switchingEmpresaKey) return;
                            // 1 conta → entra direto. N contas → abre seletor.
                            if (emp.contas.length === 1) {
                              setSwitchingEmpresaKey(emp.displayName);
                              await superSwitchCompany(emp.contas[0].id);
                            } else {
                              setContaPickerEmpresa(emp);
                            }
                          }}
                          disabled={switchingEmpresaKey === emp.displayName}
                          aria-label={`Acessar conta de ${emp.displayName}`}
                          title={emp.contas.length > 1
                            ? `Escolher 1 entre ${emp.contas.length} contas`
                            : "Entrar no contexto da conta"}
                        >
                          <LogIn size={14} strokeWidth={2} />
                          <span className={classes.trackingBtnLabel}>
                            {switchingEmpresaKey === emp.displayName ? "Acessando..." : "Acessar Conta"}
                          </span>
                        </button>
                      ))}
                      <button
                        type="button"
                        className={classes.trackingManageBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDetail(emp);
                        }}
                        aria-label={`Gerenciar empresa ${emp.displayName}`}
                        title="Administrar contas, usuários e limites desta Empresa"
                      >
                        <Settings size={14} strokeWidth={2} />
                        <span className={classes.trackingBtnLabel}>Gerenciar Empresa</span>
                      </button>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
          {renderPagination(
            Math.min(empresasPage, totalPages(empresas.length, EMPRESAS_PER_PAGE)),
            totalPages(empresas.length, EMPRESAS_PER_PAGE),
            setEmpresasPage
          )}
        </>
      )}

      <div className={classes.bottomSpacer} aria-hidden="true" />

      {renderCreateEmpresaModal()}

      {/* Modal de seleção de Conta — também precisa estar disponível na LISTA
          de Empresas (não só no detalhe), porque é onde o botão "Acessar Conta"
          fica. Sem isso, clicar no botão setava o estado mas o componente
          não estava no DOM e nada aparecia. */}
      <ContaSelectorModal
        open={!!contaPickerEmpresa}
        empresa={contaPickerEmpresa}
        onClose={() => setContaPickerEmpresa(null)}
        onSelect={async (conta) => {
          setSwitchingEmpresaKey(contaPickerEmpresa?.displayName || "");
          await superSwitchCompany(conta.id);
        }}
      />
    </div>
  );
};

export default CompaniesByGroup;
